import { useEffect, useState } from "react";
import { Search, Plus, Check, Loader2, PackagePlus } from "lucide-react";
import plannerApi from "@/lib/plannerApi";
import { fmtINR } from "@/lib/portalApi";

export const BrowsePanel = ({ plannerType, establishment, inList, onAddProduct, onAddCustom }) => {
    const [templates, setTemplates] = useState([]);
    const [tplId, setTplId] = useState("");
    const [tpl, setTpl] = useState(null);
    const [section, setSection] = useState(0);

    useEffect(() => {
        plannerApi.get("/templates", { params: { type: plannerType } }).then((r) => {
            setTemplates(r.data.templates);
            const pick = r.data.templates.find((t) => t.establishment_type === establishment) || r.data.templates[0];
            if (pick) setTplId(pick.id);
        });
    }, [plannerType, establishment]);

    useEffect(() => {
        if (!tplId) return;
        setTpl(null);
        setSection(0);
        plannerApi.get(`/templates/${tplId}/resolved`, { params: { type: plannerType } }).then((r) => setTpl(r.data));
    }, [tplId, plannerType]);

    const sec = tpl?.sections[section];
    const sectionDone = (s) => s.lines.filter((ln) => ln.matches.some((m) => inList(m.item_id)) || inList(null, ln.generic)).length;

    return (
        <div data-testid="planner-browse">
            <SearchBox plannerType={plannerType} inList={inList} onAddProduct={onAddProduct} onAddCustom={onAddCustom}/>
            <div className="flex items-center justify-between gap-2 mt-5 mb-2">
                <div className="text-xs font-black uppercase tracking-widest text-slate-500">Guided checklist</div>
                {templates.length > 1 && (
                    <select value={tplId} onChange={(e) => setTplId(e.target.value)} className="hdb-input !w-56 text-sm"
                            data-testid="planner-template-select">
                        {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                )}
            </div>
            {!tpl ? (
                <div className="p-8 text-center text-slate-400"><Loader2 className="inline animate-spin" size={16}/></div>
            ) : (
                <>
                    <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="planner-sections">
                        {tpl.sections.map((s, i) => {
                            const done = sectionDone(s);
                            return (
                                <button key={s.name} onClick={() => setSection(i)} data-testid={`planner-section-${i}`}
                                        className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${i === section ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
                                    {s.name}{done > 0 && <span className={`ml-1.5 ${i === section ? "text-emerald-300" : "text-emerald-700"}`}>{done}/{s.lines.length}</span>}
                                </button>
                            );
                        })}
                    </div>
                    <div className="space-y-2 mt-2">
                        {sec.lines.map((ln, i) => (
                            <GuidedLine key={ln.generic} ln={ln} idx={i} section={sec.name} inList={inList}
                                        onAddProduct={onAddProduct} onAddCustom={onAddCustom}/>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const GuidedLine = ({ ln, idx, section, inList, onAddProduct, onAddCustom }) => (
    <div className="rounded-xl bg-white border border-slate-200 p-3" data-testid={`planner-line-${idx}`}>
        <div className="font-bold text-slate-900 text-sm">{ln.generic}</div>
        <div className="flex flex-wrap gap-1.5 mt-2">
            {ln.matches.map((m) => {
                const on = inList(m.item_id);
                return (
                    <button key={m.item_id} onClick={() => !on && onAddProduct(m, section, ln.generic)} disabled={on}
                            data-testid={`planner-match-${m.item_id}`}
                            className={`text-xs px-2.5 py-1.5 rounded-lg border inline-flex items-center gap-1.5 transition-colors ${on ? "bg-emerald-50 border-emerald-300 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-700 hover:border-emerald-500"}`}>
                        {on ? <Check size={12}/> : <Plus size={12}/>} {m.name}
                        {m.rate > 0 && <span className="font-mono text-slate-500">{fmtINR(m.rate)}</span>}
                    </button>
                );
            })}
            {inList(null, ln.generic) ? (
                <span className="text-xs px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 inline-flex items-center gap-1"><Check size={12}/> Requested</span>
            ) : (
                <button onClick={() => onAddCustom(ln.generic, section)} data-testid={`planner-request-${idx}`}
                        className="text-xs px-2.5 py-1.5 rounded-lg border border-dashed border-slate-300 text-slate-600 hover:border-slate-500 inline-flex items-center gap-1">
                    <PackagePlus size={12}/> {ln.matches.length ? "Other brand / size" : "Not listed yet — request it"}
                </button>
            )}
        </div>
    </div>
);

const SearchBox = ({ plannerType, inList, onAddProduct, onAddCustom }) => {
    const [q, setQ] = useState("");
    const [res, setRes] = useState(null);
    useEffect(() => {
        if (q.trim().length < 2) { setRes(null); return; }
        let live = true;
        const t = setTimeout(() => plannerApi.get("/search", { params: { q, type: plannerType } })
            .then((r) => live && setRes(r.data.results)), 300);
        return () => { live = false; clearTimeout(t); };
    }, [q, plannerType]);
    return (
        <div className="relative">
            <Search size={16} className="absolute left-3 top-3.5 text-slate-400"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="planner-search"
                   placeholder="Search — atta, toor dal, sarson ka tel, phenyl…"
                   className="hdb-input !pl-10 !py-3 !text-base !rounded-xl"/>
            {res && (
                <div className="mt-2 rounded-xl bg-white border border-slate-200 divide-y divide-slate-100 max-h-80 overflow-y-auto" data-testid="planner-search-results">
                    {res.map((m) => {
                        const on = inList(m.item_id);
                        return (
                            <button key={m.item_id} disabled={on} onClick={() => onAddProduct(m, "Search", q)}
                                    data-testid={`planner-search-add-${m.item_id}`}
                                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50 disabled:opacity-60">
                                <span><span className="font-semibold text-slate-900">{m.name}</span>
                                    <span className="text-xs text-slate-400 ml-2">{m.category}</span></span>
                                <span className="flex items-center gap-2 shrink-0">
                                    <span className="font-mono text-xs text-slate-600">{fmtINR(m.rate)}</span>
                                    {on ? <Check size={14} className="text-emerald-700"/> : <Plus size={14} className="text-emerald-700"/>}
                                </span>
                            </button>
                        );
                    })}
                    <button onClick={() => { onAddCustom(q.trim(), "My items"); setQ(""); }} data-testid="planner-search-add-custom"
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-amber-800 hover:bg-amber-50">
                        <PackagePlus size={14}/> Add “{q.trim()}” as my own item {res.length === 0 && "— not in our catalogue yet"}
                    </button>
                </div>
            )}
        </div>
    );
};
